import {
    faBuilding,
    faUserPlus,
    faTicketAlt,
    faHandshake,
} from '@fortawesome/free-solid-svg-icons';

export const isServerRender = typeof window === 'undefined';

// Device type for the signup HubSpot contact. Derived from the user agent
// (not viewport width) so a desktop user with a narrow window isn't misread
// as mobile. Returns 'mobile' | 'tablet' | 'desktop'. Falls back to 'desktop'
// on the server where there's no user agent.
//
// Caveat: tablet UA detection is inherently imperfect. Modern iPadOS reports a
// desktop Safari user agent, so many iPads are still classified 'desktop' —
// treat the 'tablet' value as a floor, not an exact count.
export const getDeviceType = () => {
    if (isServerRender) { return 'desktop'; }
    const ua = navigator.userAgent;
    // Check tablet before mobile: an Android tablet UA lacks the "Mobile"
    // token that Android phones carry, so `android(?!.*mobile)` matches tablets.
    if (/ipad|tablet|(android(?!.*mobile))/i.test(ua)) { return 'tablet'; }
    if (/android|iphone|ipod|iemobile|blackberry|opera mini|mobile/i.test(ua)) { return 'mobile'; }
    return 'desktop';
};

export const useUTMParams = (location) => {
    const queryParams = new URLSearchParams(location?.search);

    const utmSource = queryParams.get('utm_source');
    const utmMedium = queryParams.get('utm_medium');
    const utmCampaign = queryParams.get('utm_campaign');
    const utmTerm = queryParams.get('utm_term');
    const utmContent = queryParams.get('utm_content');
    const utmName = queryParams.get('utm_name');
    const isSignupRequested = queryParams.has('signup');
    const hasUTMParameters = utmSource && utmMedium && utmCampaign;

    return {
        utmSource,
        utmMedium,
        utmCampaign,
        utmTerm,
        utmContent,
        utmName,
        hasUTMParameters,
        isSignupRequested
    };
};

export const useAmbassadorParams = (location) => {
    const queryParams = new URLSearchParams(location?.search);

    const mbsySource = queryParams.get('mbsy_source');
    const mbsyCampaign = queryParams.get('campaignid');
    const mbsyexp = queryParams.get('mbsy_exp');
    const shortcode = queryParams.get('mbsy');

    return {
        mbsySource,
        mbsyCampaign,
        mbsyexp,
        shortcode
    };
};

export const useAwinParams = (location) => {
    const queryParams = new URLSearchParams(location?.search);
    const awinParams = {};

    // Define all possible AWIN UTM keys
    const awinKeys = [
        'source',
        'sv1',
        'sv_campaign_id',
        'awc',
        'sn',
    ];

    // Iterate through the keys and add them to the params object if they exist
    awinKeys.forEach(key => {
        const value = queryParams.get(key);
        if (value !== null) {
            // Only add if the param exists
            awinParams[key] = value;
        }
    });

    return awinParams;
};

export const getAuthRedirectURI = (location) => {
    const queryParams = new URLSearchParams(location?.search);

    const utmSource = queryParams.get('utm_source');
    const utmMedium = queryParams.get('utm_medium');
    const utmCampaign = queryParams.get('utm_campaign');
    const utmTerm = queryParams.get('utm_term');
    const utmContent = queryParams.get('utm_content');
    const utmName = queryParams.get('utm_name');

    // Build the updated redirect_uri with existing UTM parameters
    const redirectUri = new URL(process.env.REACT_APP_AUTH0_LOGIN_REDIRECT);

    if (utmSource) {
        redirectUri.searchParams.set('utm_source', utmSource);
    }

    if (utmMedium) {
        redirectUri.searchParams.set('utm_medium', utmMedium);
    }

    if (utmCampaign) {
        redirectUri.searchParams.set('utm_campaign', utmCampaign);
    }

    if (utmTerm) {
        redirectUri.searchParams.set('utm_term', utmTerm);
    }

    if (utmContent) {
        redirectUri.searchParams.set('utm_content', utmContent);
    }

    if (utmName) {
        redirectUri.searchParams.set('utm_name', utmName);
    }

    return redirectUri.toString();
};

export const sellerOptions = [
    {
        value: 'large',
        label: 'Established Ticket Broker',
        contextLabel: 'Over $200K /yr in sales',
        icon: faBuilding,
        className: 'established',
    },
    {
        value: 'broker',
        label: 'New Ticket Broker',
        contextLabel: 'Under $200K /yr in sales',
        icon: faUserPlus,
        className: 'new',
    },
    {
        value: 'season',
        label: 'Season Ticket Holder',
        icon: faTicketAlt,
        className: 'season',
    },
    {
        value: 'small',
        label: 'One Time / Occasional',
        icon: faHandshake,
        className: 'one-time',
    },
];

export const volumeOptions = [
    {
        value: '200K_500K',
        label: '$200K - $500K',
        icon: null,
        className: 'volume-option',
    },
    {
        value: '500K_1M',
        label: '$500K - $1M',
        icon: null,
        className: 'volume-option',
    },
    {
        value: '1M_2M',
        label: '$1M - $2M',
        icon: null,
        className: 'volume-option',
    },
    {
        value: '2M_5M',
        label: '$2M - $5M',
        icon: null,
        className: 'volume-option',
    },
    {
        value: 'above_5M',
        label: 'Greater than $5M',
        icon: null,
        className: 'volume-option',
    },
];
